import React, { useContext, useState } from 'react';
import { DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { Box } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { ENTER_DATA } from 'common/constants/inputs';
import { ConfirmationDelete } from 'components/Dialogs';
import RowLabelTooltip from 'components/RowLabelTooltip/RowLabelTooltip';
import { DialogContext } from 'context';
import { useRevealEllipsisInTooltip } from 'services/hooks';
import getCancelDialogOptions from 'utilities/getCancelDialogOptions';
import { DeleteableRowHeaderProps, UseRevealEllipsisInTooltipHook } from './DeleteableRowHeader.props';
import { classes, Root } from './DeleteableRowHeader.styles';

const DeleteableRowHeader = ({
  updateObject,
  value,
  removeItem,
  isPreviousVersion,
  cell,
}: DeleteableRowHeaderProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const dialogContext = useContext(DialogContext);
  const titleValue = cell?.value ?? value ?? ENTER_DATA;
  const { tooltipRef, width } = useRevealEllipsisInTooltip() as UseRevealEllipsisInTooltipHook;

  const handleClick = () => {
    const CUSTOM_MESSAGE = `Are you sure you want to delete ${titleValue}?`;
    const { showDialog } = dialogContext;
    const ContentComponent = <ConfirmationDelete customMessage={CUSTOM_MESSAGE} itemName={titleValue} />;

    showDialog(
      getCancelDialogOptions(
        () => ContentComponent,
        () => removeItem(updateObject)
      )
    );
  };

  if (isPreviousVersion) {
    return (
      <Root ref={tooltipRef}>
        <Box
          className={classes.cellPreviousVersion}
          onMouseEnter={() => setIsHovering(false)}
          onMouseLeave={() => setIsHovering(false)}>
          <RowLabelTooltip title={value} containerWidth={width}>
            <span>{titleValue}</span>
          </RowLabelTooltip>
        </Box>
      </Root>
    );
  }

  return (
    <Root onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
      {/* This is ignored because the ref is not part of the BoxProps. It will have support in the most recent version of material-ui */}
      {/* eslint-disable @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore:next-line */}
      <Box ref={tooltipRef} className={classes.fullCell} display="flex" justifyContent="flex-start" alignItems="center">
        {isHovering && (
          <IconButton className={classes.deleteButton} onClick={handleClick}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}

        <RowLabelTooltip title={cell?.value ?? value ?? ''} containerWidth={width}>
          <span className={classes.value}>{titleValue}</span>
        </RowLabelTooltip>
      </Box>
    </Root>
  );
};

export default DeleteableRowHeader;
